import { useEffect, useState, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { setAppToken, setUser } from "../store/slices/appAuthSlice";
import { RootState } from "../store/store";
import { apiClient } from "../lib/api";
import { exitSandbox, isSandboxMode, mintSandboxSession } from "../lib/sandbox";
import { readCachedAppToken } from "../lib/appTokenStore";
import SSEManager from "./SSEManager";
import AppContent from "./AppContent";
import Loading from "./Loading";
import IdleManager from "./IdleManager";
import TokenRenewalGuard from "./TokenRenewalGuard";
import { userManager } from "../lib/oidc";

export default function ProtectedRoute() {
  // Sandbox sessions bypass OIDC entirely — the token is API-minted and the
  // visitor has no Zitadel identity. Rendering a distinct component keeps the
  // two auth flows (and their hooks) fully separated.
  if (isSandboxMode()) {
    return <SandboxGate />;
  }
  return <OidcProtectedRoute />;
}

/**
 * SandboxGate hydrates the Redux user from /auth/me using the cached sandbox
 * token, transparently re-minting an anonymous session when the token is
 * missing or expired. No IdleManager: the short server-side TTL bounds the
 * session and an idle logout would only break the demo.
 */
function SandboxGate() {
  const dispatch = useDispatch();
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    const fetchMe = (token: string) =>
      apiClient.get("/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

    (async () => {
      try {
        let token = readCachedAppToken();
        let me;
        try {
          if (!token) throw new Error("no cached sandbox token");
          me = await fetchMe(token);
        } catch {
          // Expired or missing — mint a fresh anonymous session.
          token = await mintSandboxSession();
          me = await fetchMe(token);
        }
        if (cancelled) return;
        dispatch(setAppToken(token!));
        dispatch(setUser({ ...me.data, is_new_signup: false }));
        setState("ready");
      } catch {
        if (!cancelled) {
          exitSandbox();
          setState("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (state === "loading") return <Loading />;
  if (state === "error") return <Navigate to="/login" replace />;

  return (
    <Suspense fallback={<Loading />}>
      <SSEManager />
      <AppContent />
    </Suspense>
  );
}

function OidcProtectedRoute() {
  const dispatch = useDispatch();
  const auth = useAuth();
  const [userReady, setUserReady] = useState(false);
  const appToken = useSelector(
    (state: RootState) => state.appAuthReducer.appToken,
  );

  // Keep Redux in sync with the OIDC access token so the Axios request
  // interceptor can attach the latest token to every API call.
  // automaticSilentRenew in the OIDC config ensures the token stays fresh.
  useEffect(() => {
    if (auth.user?.access_token) {
      dispatch(setAppToken(auth.user.access_token));
    }
  }, [auth.user?.access_token, dispatch]);

  // App-side logout (idle timeout, auth failure) clears the Redux token but
  // used to leave the OIDC session alive — the app then hung on a loading
  // screen forever instead of returning to /login. When the Redux session
  // ends while OIDC still considers the user signed in, drop the OIDC user
  // so the normal "not authenticated -> /login" redirect takes over.
  // Skipped while an OIDC navigation (e.g. signoutRedirect from the user
  // menu) is in flight, since that flow needs the stored user untouched.
  useEffect(() => {
    if (
      userReady &&
      !appToken &&
      auth.isAuthenticated &&
      !auth.activeNavigator
    ) {
      void auth.removeUser();
    }
  }, [appToken, auth.isAuthenticated, userReady, auth]);

  // After the OIDC token is available, fetch /auth/me to:
  //  1. JIT-provision the user+org in the DB if this is their first login, and
  //  2. Populate the Redux user object so the rest of the app has orgId/permissions.
  //
  // First-token problem: on first login the JWT has no role claims yet because
  // Zitadel only stamps them after our backend calls AddUserGrant during JIT
  // provisioning. The backend resolves this transparently — it injects the
  // correct permissions from the Redis-cached role on every request, so the
  // frontend gets a fully-populated user object on the very first /auth/me call
  // and can render the app immediately without any blocking token refresh.
  //
  // The signinSilent() call below is purely a background best-effort to ensure
  // the OIDC library's automatic renewal cycle will use a JWT that already
  // carries the role claim (so the backend cache fallback is no longer needed
  // once the token rotates). It never blocks rendering or redirects the user.
  useEffect(() => {
    if (!auth.user?.access_token) {
      setUserReady(false);
      return;
    }
    let cancelled = false;
    const token = auth.user.access_token;

    (async () => {
      try {
        const meRes = await apiClient.get("/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        // Always dispatch immediately — permissions are already correct because
        // the backend injects them from the Redis-cached role for role-less JWTs.
        dispatch(setUser({ ...meRes.data, is_new_signup: false }));

        if (meRes.data.is_new_signup) {
          // Fire-and-forget: refresh in the background so the OIDC library's
          // next automatic renewal produces a token with the role claim baked in.
          // We intentionally do NOT await this — the user is already in the app.
          // Driving the UserManager directly (rather than auth.signinSilent())
          // keeps `isLoading` false, so this best-effort call cannot flash the
          // full-screen spinner or leave a global auth error behind if it fails.
          userManager
            .signinSilent()
            .then((refreshedUser) => {
              if (cancelled || !refreshedUser?.access_token) return;
              dispatch(setAppToken(refreshedUser.access_token));
            })
            .catch(() => {
              // Silent refresh is best-effort. The backend fallback handles
              // subsequent requests with the stale token via the cached role.
            });
        }
      } catch {
        // Even on error show the app — downstream requests will surface the issue.
      } finally {
        if (!cancelled) setUserReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [auth.user?.access_token, dispatch]);

  if (auth.isLoading || (auth.isAuthenticated && !userReady)) {
    return <Loading />;
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <Suspense fallback={<Loading />}>
        <TokenRenewalGuard />
        <IdleManager />
        <SSEManager />
        <AppContent />
      </Suspense>
    </>
  );
}
